import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { 
  Award, Plus, Search, GraduationCap, DollarSign, Users, 
  FileText, CheckCircle, XCircle, Clock, Building2, Edit, 
  Trash2, Eye, RefreshCw, TrendingUp, IndianRupee
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ScholarshipManagement() {
  const [activeTab, setActiveTab] = useState('schemes');
  const [schemes, setSchemes] = useState([]);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [schemeFilter, setSchemeFilter] = useState('all');
  
  const [showSchemeModal, setShowSchemeModal] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingScheme, setEditingScheme] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  
  const [schemeForm, setSchemeForm] = useState({
    name: '', type: 'merit', provider: 'institution', description: '',
    eligibility_criteria: '', min_percentage: '', max_family_income: '',
    amount: '', amount_type: 'fixed', max_beneficiaries: '',
    applicable_categories: [], documents_required: [], is_active: true
  });
  
  const [applicationForm, setApplicationForm] = useState({
    scheme_id: '', student_id: '', academic_year: new Date().getFullYear().toString(),
    percentage: '', family_income: '', remarks: ''
  });
  
  const [statusForm, setStatusForm] = useState({
    status: '', remarks: '', approved_amount: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSchemes();
    fetchStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'applications') {
      fetchApplications();
    }
  }, [activeTab, statusFilter, schemeFilter]);

  const fetchSchemes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/scholarships/schemes`, { headers });
      setSchemes(response.data.schemes || []);
    } catch (err) {
      toast.error('Failed to fetch schemes');
    }
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (schemeFilter && schemeFilter !== 'all') params.append('scheme_id', schemeFilter);
      
      const response = await axios.get(`${API_URL}/api/scholarships/applications?${params}`, { headers });
      setApplications(response.data.applications || []);
    } catch (err) {
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/scholarships/stats`, { headers });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/students?status=Active&limit=500`, { headers });
      setStudents(response.data.students || []);
    } catch (err) {
      toast.error('Failed to fetch students');
    }
  };

  const handleSchemeSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...schemeForm,
        amount: parseFloat(schemeForm.amount),
        min_percentage: schemeForm.min_percentage ? parseFloat(schemeForm.min_percentage) : null,
        max_family_income: schemeForm.max_family_income ? parseFloat(schemeForm.max_family_income) : null,
        max_beneficiaries: schemeForm.max_beneficiaries ? parseInt(schemeForm.max_beneficiaries) : null
      };

      if (editingScheme) {
        await axios.put(`${API_URL}/api/scholarships/schemes/${editingScheme.id}`, payload, { headers });
        toast.success('Scheme updated');
      } else {
        await axios.post(`${API_URL}/api/scholarships/schemes`, payload, { headers });
        toast.success('Scheme created');
      }
      
      setShowSchemeModal(false);
      setEditingScheme(null);
      resetSchemeForm();
      fetchSchemes();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...applicationForm,
        percentage: applicationForm.percentage ? parseFloat(applicationForm.percentage) : null,
        family_income: applicationForm.family_income ? parseFloat(applicationForm.family_income) : null
      };

      await axios.post(`${API_URL}/api/scholarships/applications`, payload, { headers });
      toast.success('Application submitted');
      
      setShowApplicationModal(false);
      resetApplicationForm();
      fetchApplications();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedApplication) return;
    
    try {
      const payload = {
        status: statusForm.status,
        remarks: statusForm.remarks,
        approved_amount: statusForm.approved_amount ? parseFloat(statusForm.approved_amount) : null
      };

      await axios.put(
        `${API_URL}/api/scholarships/applications/${selectedApplication.id}/status`,
        payload,
        { headers }
      );
      
      toast.success(`Application ${statusForm.status}`);
      setShowStatusModal(false);
      setSelectedApplication(null);
      fetchApplications();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Status update failed');
    }
  };

  const handleDeleteScheme = async (id) => {
    if (!window.confirm('Delete this scheme?')) return;
    try {
      await axios.delete(`${API_URL}/api/scholarships/schemes/${id}`, { headers });
      toast.success('Scheme deleted');
      fetchSchemes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const resetSchemeForm = () => {
    setSchemeForm({
      name: '', type: 'merit', provider: 'institution', description: '',
      eligibility_criteria: '', min_percentage: '', max_family_income: '',
      amount: '', amount_type: 'fixed', max_beneficiaries: '',
      applicable_categories: [], documents_required: [], is_active: true
    });
  };

  const resetApplicationForm = () => {
    setApplicationForm({
      scheme_id: '', student_id: '', academic_year: new Date().getFullYear().toString(),
      percentage: '', family_income: '', remarks: ''
    });
  };

  const openEditScheme = (scheme) => {
    setEditingScheme(scheme);
    setSchemeForm({
      name: scheme.name || '',
      type: scheme.type || 'merit',
      provider: scheme.provider || 'institution',
      description: scheme.description || '',
      eligibility_criteria: scheme.eligibility_criteria || '',
      min_percentage: scheme.min_percentage || '',
      max_family_income: scheme.max_family_income || '',
      amount: scheme.amount || '',
      amount_type: scheme.amount_type || 'fixed',
      max_beneficiaries: scheme.max_beneficiaries || '',
      applicable_categories: scheme.applicable_categories || [],
      documents_required: scheme.documents_required || [],
      is_active: scheme.is_active !== false
    });
    setShowSchemeModal(true);
  };

  const openStatusModal = (app, newStatus) => {
    setSelectedApplication(app);
    setStatusForm({
      status: newStatus,
      remarks: '',
      approved_amount: app.applied_amount || ''
    });
    setShowStatusModal(true);
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    under_review: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    disbursed: 'bg-purple-100 text-purple-800'
  };

  const typeColors = {
    merit: 'bg-blue-100 text-blue-800',
    need_based: 'bg-green-100 text-green-800',
    government: 'bg-purple-100 text-purple-800',
    private: 'bg-orange-100 text-orange-800',
    sports: 'bg-red-100 text-red-800',
    minority: 'bg-cyan-100 text-cyan-800'
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue', subtitle }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 bg-${color}-100 rounded-full flex items-center justify-center`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 space-y-6" data-testid="scholarship-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Scholarship Management</h1>
          <p className="text-gray-500">Manage scholarship schemes and student applications</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => { fetchStudents(); resetApplicationForm(); setShowApplicationModal(true); }}>
            <FileText className="h-4 w-4 mr-2" />
            New Application
          </Button>
          <Button onClick={() => { resetSchemeForm(); setEditingScheme(null); setShowSchemeModal(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Scheme
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <StatCard title="Total Schemes" value={stats.schemes?.total || 0} icon={Award} color="blue" />
          <StatCard title="Active Schemes" value={stats.schemes?.active || 0} icon={CheckCircle} color="green" />
          <StatCard title="Applications" value={stats.applications?.total || 0} icon={FileText} color="purple" />
          <StatCard title="Pending" value={stats.applications?.pending || 0} icon={Clock} color="yellow" />
          <StatCard title="Beneficiaries" value={stats.disbursement?.beneficiaries || 0} icon={Users} color="cyan" />
          <StatCard 
            title="Disbursed" 
            value={`₹${((stats.disbursement?.total_amount || 0) / 100000).toFixed(1)}L`} 
            icon={IndianRupee} 
            color="green"
          />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="schemes">Scholarship Schemes</TabsTrigger>
          <TabsTrigger value="applications">Applications</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* Schemes Tab */}
        <TabsContent value="schemes" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {schemes.length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="p-8 text-center text-gray-500">
                  No scholarship schemes found. Create one to get started.
                </CardContent>
              </Card>
            ) : (
              schemes.map((scheme) => (
                <Card key={scheme.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{scheme.name}</CardTitle>
                        <p className="text-sm text-gray-500">{scheme.code}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeColors[scheme.type] || 'bg-gray-100'}`}>
                        {scheme.type?.replace('_', ' ')}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Amount:</span>
                        <span className="font-bold text-green-600">₹{scheme.amount?.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Provider:</span>
                        <span className="capitalize">{scheme.provider}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Applications:</span>
                        <span>{scheme.applications_count || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Approved:</span>
                        <span className="text-green-600">{scheme.approved_count || 0}</span>
                      </div>
                      
                      {scheme.description && (
                        <p className="text-sm text-gray-600 pt-2 border-t">{scheme.description}</p>
                      )}
                      
                      <div className="flex gap-2 pt-2">
                        <Button variant="outline" size="sm" onClick={() => openEditScheme(scheme)}>
                          <Edit className="h-4 w-4 mr-1" /> Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteScheme(scheme.id)}>
                          <Trash2 className="h-4 w-4 mr-1 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Applications Tab */}
        <TabsContent value="applications" className="mt-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="disbursed">Disbursed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={schemeFilter} onValueChange={setSchemeFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Scheme" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Schemes</SelectItem>
                {schemes.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Applications Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">Application #</th>
                      <th className="px-4 py-3 text-left">Student</th>
                      <th className="px-4 py-3 text-left">Scheme</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                      <th className="px-4 py-3 text-center">Status</th>
                      <th className="px-4 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="text-center py-8">
                          <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
                        </td>
                      </tr>
                    ) : applications.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-8 text-gray-500">
                          No applications found
                        </td>
                      </tr>
                    ) : (
                      applications.map((app) => (
                        <tr key={app.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-sm">{app.application_number}</td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium">{app.student?.full_name || app.student?.name}</p>
                              <p className="text-xs text-gray-500">{app.student?.student_id}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium">{app.scheme?.name}</p>
                              <p className="text-xs text-gray-500">{app.scheme?.code}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <p>₹{app.applied_amount?.toLocaleString('en-IN')}</p>
                            {app.approved_amount && app.approved_amount !== app.applied_amount && (
                              <p className="text-xs text-green-600">Approved: ₹{app.approved_amount.toLocaleString('en-IN')}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[app.status]}`}>
                              {app.status?.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              {app.status === 'pending' && (
                                <>
                                  <Button size="sm" variant="ghost" onClick={() => openStatusModal(app, 'approved')}>
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => openStatusModal(app, 'rejected')}>
                                    <XCircle className="h-4 w-4 text-red-600" />
                                  </Button>
                                </>
                              )}
                              {app.status === 'approved' && (
                                <Button size="sm" variant="ghost" onClick={() => openStatusModal(app, 'disbursed')}>
                                  <DollarSign className="h-4 w-4 text-purple-600" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Type-wise Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Scheme Types</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.type_wise_schemes?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.type_wise_schemes.map((item, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${typeColors[item.type] || 'bg-gray-100'}`}>
                          {item.type?.replace('_', ' ')}
                        </span>
                        <div className="text-right">
                          <span className="font-bold">{item.count} schemes</span>
                          <p className="text-xs text-gray-500">₹{item.total_amount?.toLocaleString('en-IN')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No data available</p>
                )}
              </CardContent>
            </Card>

            {/* Application Status */}
            <Card>
              <CardHeader>
                <CardTitle>Application Status</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.applications ? (
                  <div className="space-y-3">
                    {Object.entries(stats.applications).filter(([k]) => k !== 'total').map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[status] || 'bg-gray-100'}`}>
                          {status.replace('_', ' ')}
                        </span>
                        <span className="text-lg font-bold">{count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No data available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Scheme Modal */}
      <Dialog open={showSchemeModal} onOpenChange={setShowSchemeModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingScheme ? 'Edit Scheme' : 'Create Scholarship Scheme'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSchemeSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium">Scheme Name *</label>
                <Input
                  value={schemeForm.name}
                  onChange={(e) => setSchemeForm({...schemeForm, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Type *</label>
                <Select value={schemeForm.type} onValueChange={(v) => setSchemeForm({...schemeForm, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="merit">Merit Based</SelectItem>
                    <SelectItem value="need_based">Need Based</SelectItem>
                    <SelectItem value="government">Government</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="sports">Sports</SelectItem>
                    <SelectItem value="minority">Minority</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Provider *</label>
                <Select value={schemeForm.provider} onValueChange={(v) => setSchemeForm({...schemeForm, provider: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="institution">Institution</SelectItem>
                    <SelectItem value="government">Government</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="ngo">NGO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Amount (₹) *</label>
                <Input
                  type="number"
                  value={schemeForm.amount}
                  onChange={(e) => setSchemeForm({...schemeForm, amount: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Min Percentage</label>
                <Input
                  type="number"
                  step="0.1"
                  value={schemeForm.min_percentage}
                  onChange={(e) => setSchemeForm({...schemeForm, min_percentage: e.target.value})}
                  placeholder="e.g., 75"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Max Family Income (₹)</label>
                <Input
                  type="number"
                  value={schemeForm.max_family_income}
                  onChange={(e) => setSchemeForm({...schemeForm, max_family_income: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Max Beneficiaries</label>
                <Input
                  type="number"
                  value={schemeForm.max_beneficiaries}
                  onChange={(e) => setSchemeForm({...schemeForm, max_beneficiaries: e.target.value})}
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  value={schemeForm.description}
                  onChange={(e) => setSchemeForm({...schemeForm, description: e.target.value})}
                  rows={2}
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Eligibility Criteria</label>
                <Textarea
                  value={schemeForm.eligibility_criteria}
                  onChange={(e) => setSchemeForm({...schemeForm, eligibility_criteria: e.target.value})}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSchemeModal(false)}>Cancel</Button>
              <Button type="submit">{editingScheme ? 'Update' : 'Create'} Scheme</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Application Modal */}
      <Dialog open={showApplicationModal} onOpenChange={setShowApplicationModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Scholarship Application</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleApplicationSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Scheme *</label>
              <Select value={applicationForm.scheme_id} onValueChange={(v) => setApplicationForm({...applicationForm, scheme_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select scheme" /></SelectTrigger>
                <SelectContent>
                  {schemes.filter(s => s.is_active).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} (₹{s.amount})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Student *</label>
              <Select value={applicationForm.student_id} onValueChange={(v) => setApplicationForm({...applicationForm, student_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>
                  {students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.full_name || s.name} ({s.student_id})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Academic Year</label>
                <Input
                  value={applicationForm.academic_year}
                  onChange={(e) => setApplicationForm({...applicationForm, academic_year: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Percentage</label>
                <Input
                  type="number"
                  step="0.1"
                  value={applicationForm.percentage}
                  onChange={(e) => setApplicationForm({...applicationForm, percentage: e.target.value})}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Family Income (₹)</label>
              <Input
                type="number"
                value={applicationForm.family_income}
                onChange={(e) => setApplicationForm({...applicationForm, family_income: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Remarks</label>
              <Textarea
                value={applicationForm.remarks}
                onChange={(e) => setApplicationForm({...applicationForm, remarks: e.target.value})}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowApplicationModal(false)}>Cancel</Button>
              <Button type="submit">Submit Application</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Status Update Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {statusForm.status === 'approved' && 'Approve Application'}
              {statusForm.status === 'rejected' && 'Reject Application'}
              {statusForm.status === 'disbursed' && 'Mark as Disbursed'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {statusForm.status === 'approved' && (
              <div>
                <label className="text-sm font-medium">Approved Amount (₹)</label>
                <Input
                  type="number"
                  value={statusForm.approved_amount}
                  onChange={(e) => setStatusForm({...statusForm, approved_amount: e.target.value})}
                />
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Remarks</label>
              <Textarea
                value={statusForm.remarks}
                onChange={(e) => setStatusForm({...statusForm, remarks: e.target.value})}
                placeholder={statusForm.status === 'rejected' ? 'Reason for rejection...' : 'Optional remarks...'}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusModal(false)}>Cancel</Button>
            <Button 
              onClick={handleStatusUpdate}
              className={statusForm.status === 'rejected' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {statusForm.status === 'approved' && 'Approve'}
              {statusForm.status === 'rejected' && 'Reject'}
              {statusForm.status === 'disbursed' && 'Mark Disbursed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
